import { useEffect, useRef, useState } from 'react';
import { Box, chakra, Flex, Text } from '@chakra-ui/react';
import { INTRO_ICON_OPTIONS, type IntroIconId } from '@denise-augusta/domain';
import { IntroIcon } from '@denise-augusta/ui';

/**
 * Icon picker — a custom dropdown so the options can show the actual icon
 * next to its label (native <select> can't render SVGs).
 */
export function IconSelect({
  label,
  value,
  onChange,
  baseline,
}: {
  label?: string;
  value: IntroIconId;
  onChange: (next: IntroIconId) => void;
  /** Published value to diff against — when it differs, the trigger is highlighted green. */
  baseline?: IntroIconId;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = INTRO_ICON_OPTIONS.find((option) => option.id === value) ?? INTRO_ICON_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <Box ref={rootRef} position="relative" w="100%">
      {label && (
        <Text fontSize="0.66rem" fontWeight="600" letterSpacing="0.14em" textTransform="uppercase" color="brand.ink" mb="0.35rem">
          {label}
        </Text>
      )}
      <chakra.button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        display="flex"
        alignItems="center"
        gap="0.6rem"
        w="100%"
        px="0.9rem"
        py="0.6rem"
        bg="paper.white"
        border="1px solid"
        borderColor={open ? 'brand.mid' : baseline !== undefined && baseline !== value ? '#8FBF3F' : 'paper.border'}
        boxShadow={baseline !== undefined && baseline !== value && !open ? '0 0 0 1px #B5E061' : undefined}
        borderRadius="8px"
        fontSize="0.85rem"
        color="ink.body"
        cursor="pointer"
        _hover={{ borderColor: 'brand.mid' }}
      >
        <Box color="brand.mid" display="inline-flex">
          <IntroIcon id={selected.id} size={16} />
        </Box>
        <Box as="span" flex="1" textAlign="left">
          {selected.label}
        </Box>
        <Box as="span" fontSize="0.65rem" color="ink.muted" transform={open ? 'rotate(180deg)' : undefined} transition="transform 0.15s">
          ▼
        </Box>
      </chakra.button>

      {open && (
        <Box
          role="listbox"
          position="absolute"
          top="calc(100% + 4px)"
          left="0"
          right="0"
          zIndex="50"
          bg="paper.white"
          border="1px solid"
          borderColor="paper.border"
          borderRadius="10px"
          boxShadow="0 12px 32px rgba(31,45,92,0.16)"
          maxH="260px"
          overflowY="auto"
          py="0.3rem"
        >
          {INTRO_ICON_OPTIONS.map((option) => (
            <Flex
              key={option.id}
              role="option"
              aria-selected={option.id === value}
              onClick={() => {
                onChange(option.id);
                setOpen(false);
              }}
              align="center"
              gap="0.6rem"
              px="0.9rem"
              py="0.45rem"
              fontSize="0.85rem"
              color={option.id === value ? 'brand.mid' : 'ink.body'}
              bg={option.id === value ? 'brand.wash' : 'transparent'}
              cursor="pointer"
              _hover={{ bg: 'brand.wash' }}
            >
              <Box color="brand.mid" display="inline-flex">
                <IntroIcon id={option.id} size={16} />
              </Box>
              {option.label}
              {option.id === value && (
                <Box as="span" ml="auto" fontSize="0.75rem">
                  ✓
                </Box>
              )}
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}
